import { PORT_STATUS } from './tn-porting';
import type { TnPortOrderDetails } from './tn-porting';

// Group Port Order Status model (POR-132). The authoritative spec is
// thoughts/shared/research/2026-08-10-POR-132-group-port-order-status-spec.md.
//
// Three concepts stay separate in the data model:
//   Timeline distribution + Group Summary Status + Requires Action
// and the UI composes the user-facing Combined Status from the last two. There
// are deliberately no combined `ACTION_REQUIRED_*` enum values (spec §7).
//
// This is the single source of truth for the computation. cpaas-api uses it for
// the group details response and the Angular apps use it for client-side
// grouping/sorting; the orders-list SQL in cpaas-backend-numbering mirrors the
// same predicates (it has to aggregate before pagination) and is cross-checked
// against this function by unit test.

export const GROUP_SUMMARY_STATUS = {
  NEW: 'NEW',
  FOC_PENDING: 'FOC_PENDING',
  PARTIAL_FOC: 'PARTIAL_FOC',
  FOC_RECEIVED: 'FOC_RECEIVED',
  PENDING_ACTIVATION: 'PENDING_ACTIVATION',
  PARTIALLY_COMPLETE: 'PARTIALLY_COMPLETE',
  COMPLETE: 'COMPLETE',
  CANCELLED: 'CANCELLED',
} as const;

export type GroupSummaryStatus = typeof GROUP_SUMMARY_STATUS[keyof typeof GROUP_SUMMARY_STATUS];

// Display labels for the summary statuses. Titleizing the enum values would
// render "Foc Pending", so the labels are spelled out here and shared by the
// orders-list filter options and the detail page's Combined Status chip.
export const GROUP_SUMMARY_STATUS_LABELS: Record<GroupSummaryStatus, string> = {
  [GROUP_SUMMARY_STATUS.NEW]: 'New',
  [GROUP_SUMMARY_STATUS.FOC_PENDING]: 'FOC Pending',
  [GROUP_SUMMARY_STATUS.PARTIAL_FOC]: 'Partial FOC',
  [GROUP_SUMMARY_STATUS.FOC_RECEIVED]: 'FOC Received',
  [GROUP_SUMMARY_STATUS.PENDING_ACTIVATION]: 'Pending Activation',
  [GROUP_SUMMARY_STATUS.PARTIALLY_COMPLETE]: 'Partially Complete',
  [GROUP_SUMMARY_STATUS.COMPLETE]: 'Complete',
  [GROUP_SUMMARY_STATUS.CANCELLED]: 'Cancelled',
};

/**
 * Sentinels accepted by the orders-list status filter alongside the eight
 * summary statuses. They are not statuses: ACTION_REQUIRED narrows to groups
 * with a REJECTED child (the customer's to fix — POR-153 narrowed it from the
 * full action-required set) and SUBMISSION_ISSUE to groups with a child in a
 * submission-issue status (Nuso's to fix). The two OR-combine with each other
 * and AND-combine with any status selection (spec §6/§7).
 */
export const ACTION_REQUIRED_FILTER_VALUE = 'ACTION_REQUIRED';
export const SUBMISSION_ISSUE_FILTER_VALUE = 'SUBMISSION_ISSUE';

export const ACTION_REQUIRED_LABEL = 'Action Required';
export const SUBMISSION_ISSUE_LABEL = 'Submission Issue';

/**
 * The attention line composed from the two POR-153 flags: REJECTED (the
 * customer's to fix) outranks a submission issue (Nuso's); null when neither is
 * present. Every surface that pairs the flags goes through this so the
 * precedence lives in one place.
 */
export function attentionLabelFor(
  needsAttention: boolean,
  hasSubmissionIssue: boolean,
): string | null {
  if (needsAttention) return ACTION_REQUIRED_LABEL;
  if (hasSubmissionIssue) return SUBMISSION_ISSUE_LABEL;
  return null;
}

export const TIMELINE_STAGE = {
  NEW: 'NEW',
  FOC_PENDING: 'FOC_PENDING',
  FOC_RECEIVED: 'FOC_RECEIVED',
  PENDING_ACTIVATION: 'PENDING_ACTIVATION',
  COMPLETE: 'COMPLETE',
} as const;

export type TimelineStage = typeof TIMELINE_STAGE[keyof typeof TIMELINE_STAGE];

// The five fixed lifecycle stages, in order. Action Required, Partial FOC,
// Partially Complete and Cancelled are NOT stages (spec §1).
export const TIMELINE_STAGES: readonly TimelineStage[] = [
  TIMELINE_STAGE.NEW,
  TIMELINE_STAGE.FOC_PENDING,
  TIMELINE_STAGE.FOC_RECEIVED,
  TIMELINE_STAGE.PENDING_ACTIVATION,
  TIMELINE_STAGE.COMPLETE,
];

// POR-153 splits "somebody has to intervene" (spec §6) by who fixes it. REJECTED is the
// customer's to fix (edit the order); these statuses are Nuso's (resubmit / ops).
export const SUBMISSION_ISSUE_STATUSES: readonly string[] = [
  PORT_STATUS.SUBMISSION_FAILED,
  PORT_STATUS.ACTIVATION_CONFLICT,
  PORT_STATUS.ACTIVATION_FAILED,
  PORT_STATUS.FAILED,
  PORT_STATUS.CANCELLATION_FAILED,
  PORT_STATUS.PARTIAL_COMPLETE,
];

// Child statuses that mean somebody has to intervene (spec §6): the customer's
// REJECTED plus Nuso's submission issues, derived so the POR-153 split can never
// drift from the union. An order in one of these still counts at its
// milestone-derived timeline stage — the attention bar adds to the lifecycle
// counts, it never replaces them.
export const ACTION_REQUIRED_STATUSES: readonly string[] = [
  PORT_STATUS.REJECTED,
  ...SUBMISSION_ISSUE_STATUSES,
];

// Milestone: the order has entered activation. Anything at or past this point
// has necessarily cleared FOC (spec §9).
export const ENTERED_ACTIVATION_STATUSES: readonly string[] = [
  PORT_STATUS.ACTIVATION_PENDING,
  PORT_STATUS.ACTIVATION_IN_PROGRESS,
  PORT_STATUS.ACTIVATION_CONFLICT,
  PORT_STATUS.ACTIVATION_FAILED,
  PORT_STATUS.PARTIAL_COMPLETE,
  PORT_STATUS.COMPLETED,
];

// Statuses that by themselves prove FOC was cleared, for orders whose foc_date
// is not set. INTERNAL orders are born FOC_RECEIVED with a null foc_date, so
// this fallback is required rather than merely defensive. LSR_APPROVED counts
// as cleared FOC, matching the rollups this model replaces.
export const CLEARED_FOC_STATUSES: readonly string[] = [
  PORT_STATUS.FOC_RECEIVED,
  PORT_STATUS.LSR_APPROVED,
];

// Statuses that mean the order is somewhere inside the FOC process without
// having cleared it. REJECTED sits here when the order never held an FOC; a
// rejection after FOC keeps its foc_date and so counts as cleared (spec §12
// footnote). The update/date-change/cancellation-pending statuses never move an
// order backward (spec §11) — they land here only when no earlier milestone was
// reached.
export const ENTERED_FOC_PROCESS_STATUSES: readonly string[] = [
  PORT_STATUS.FOC_PENDING,
  PORT_STATUS.UPDATE_POSTED,
  PORT_STATUS.UPDATE_RECEIVED,
  PORT_STATUS.UPDATE_PENDING_FOC,
  PORT_STATUS.DATE_CHANGE_POSTED,
  PORT_STATUS.DATE_CHANGE_PENDING,
  PORT_STATUS.CANCELLATION_PENDING,
  PORT_STATUS.REJECTED,
];

export interface GroupStatusChildInput {
  status: string | null;
  focDate: Date | string | null;
}

export interface PortOrderGroupStatus {
  groupSummaryStatus: GroupSummaryStatus;
  requiresAction: boolean;
  actionRequiredCount: number;
  // Timeline distribution over applicable orders. Sums to applicableOrderCount.
  newCount: number;
  focPendingCount: number;
  focReceivedCount: number;
  pendingActivationCount: number;
  completeCount: number;
  totalOrderCount: number;
  applicableOrderCount: number;
  // Milestone tallies driving the "6 of 10 FOCs received" style sublabels.
  focClearedCount: number;
  completedCount: number;
}

/**
 * Shape of the tnPortOrderGroupDetails response. The query resolves from either
 * a child order id or the parent group id; `groupId` is null for an ungrouped
 * single order, which stands in for its own group.
 */
export interface PortOrderGroupDetails {
  groupId: string | null;
  groupDisplayId: string;
  groupStatus: PortOrderGroupStatus;
  orders: TnPortOrderDetails[];
}

function has(statuses: readonly string[], status: string | null): boolean {
  return !!status && statuses.includes(status);
}

// Successfully cancelled orders drop out of every lifecycle calculation
// (spec §10). CANCELLATION_PENDING and CANCELLATION_FAILED are not cancelled.
export function isApplicableOrder(child: GroupStatusChildInput): boolean {
  return child.status !== PORT_STATUS.CANCELLED;
}

export function hasEnteredActivation(child: GroupStatusChildInput): boolean {
  return has(ENTERED_ACTIVATION_STATUSES, child.status);
}

export function hasClearedFoc(child: GroupStatusChildInput): boolean {
  return (
    hasEnteredActivation(child) ||
    has(CLEARED_FOC_STATUSES, child.status) ||
    child.focDate != null
  );
}

export function hasEnteredFocProcess(child: GroupStatusChildInput): boolean {
  return hasClearedFoc(child) || has(ENTERED_FOC_PROCESS_STATUSES, child.status);
}

export function isActionRequiredStatus(status: string | null | undefined): boolean {
  return has(ACTION_REQUIRED_STATUSES, status ?? null);
}

export function isRejectedStatus(status: string | null | undefined): boolean {
  return status === PORT_STATUS.REJECTED;
}

export function isSubmissionIssueStatus(status: string | null | undefined): boolean {
  return has(SUBMISSION_ISSUE_STATUSES, status ?? null);
}

// Where a single order sits on the five-stage timeline, by last milestone
// achieved rather than by literal current status. An ACTIVATION_FAILED order
// still counts at Pending Activation; a REJECTED order counts at FOC Received
// when it holds an FOC and at FOC Pending otherwise.
export function timelineStageForOrder(child: GroupStatusChildInput): TimelineStage {
  if (child.status === PORT_STATUS.COMPLETED) return TIMELINE_STAGE.COMPLETE;
  if (hasEnteredActivation(child)) return TIMELINE_STAGE.PENDING_ACTIVATION;
  if (hasClearedFoc(child)) return TIMELINE_STAGE.FOC_RECEIVED;
  if (hasEnteredFocProcess(child)) return TIMELINE_STAGE.FOC_PENDING;
  return TIMELINE_STAGE.NEW;
}

export function computePortOrderGroupStatus(
  children: readonly GroupStatusChildInput[],
): PortOrderGroupStatus {
  const all = children ?? [];
  const applicable = all.filter(isApplicableOrder);

  const stageCounts: Record<TimelineStage, number> = {
    [TIMELINE_STAGE.NEW]: 0,
    [TIMELINE_STAGE.FOC_PENDING]: 0,
    [TIMELINE_STAGE.FOC_RECEIVED]: 0,
    [TIMELINE_STAGE.PENDING_ACTIVATION]: 0,
    [TIMELINE_STAGE.COMPLETE]: 0,
  };
  for (const child of applicable) {
    stageCounts[timelineStageForOrder(child)]++;
  }

  const completedCount = applicable.filter(c => c.status === PORT_STATUS.COMPLETED).length;
  const focClearedCount = applicable.filter(hasClearedFoc).length;
  const enteredActivationCount = applicable.filter(hasEnteredActivation).length;
  const enteredFocProcessCount = applicable.filter(hasEnteredFocProcess).length;
  const actionRequiredCount = applicable.filter(c => isActionRequiredStatus(c.status)).length;

  return {
    groupSummaryStatus: summaryStatus({
      totalCount: all.length,
      applicableCount: applicable.length,
      completedCount,
      focClearedCount,
      enteredActivationCount,
      enteredFocProcessCount,
    }),
    requiresAction: actionRequiredCount > 0,
    actionRequiredCount,
    newCount: stageCounts[TIMELINE_STAGE.NEW],
    focPendingCount: stageCounts[TIMELINE_STAGE.FOC_PENDING],
    focReceivedCount: stageCounts[TIMELINE_STAGE.FOC_RECEIVED],
    pendingActivationCount: stageCounts[TIMELINE_STAGE.PENDING_ACTIVATION],
    completeCount: stageCounts[TIMELINE_STAGE.COMPLETE],
    totalOrderCount: all.length,
    applicableOrderCount: applicable.length,
    focClearedCount,
    completedCount,
  };
}

// Spec §3, evaluated in priority order. The SQL mirror in
// cpaas-port-order-group-status-sql.ts implements these same branches in the
// same order over aggregates.
function summaryStatus({
  totalCount,
  applicableCount,
  completedCount,
  focClearedCount,
  enteredActivationCount,
  enteredFocProcessCount,
}: {
  totalCount: number;
  applicableCount: number;
  completedCount: number;
  focClearedCount: number;
  enteredActivationCount: number;
  enteredFocProcessCount: number;
}): GroupSummaryStatus {
  // An empty group has nothing to roll up; a group whose every order was
  // cancelled is Cancelled.
  if (totalCount === 0) return GROUP_SUMMARY_STATUS.NEW;
  if (applicableCount === 0) return GROUP_SUMMARY_STATUS.CANCELLED;

  if (completedCount === applicableCount) return GROUP_SUMMARY_STATUS.COMPLETE;
  if (completedCount > 0) return GROUP_SUMMARY_STATUS.PARTIALLY_COMPLETE;
  if (focClearedCount > 0 && focClearedCount < applicableCount) {
    return GROUP_SUMMARY_STATUS.PARTIAL_FOC;
  }
  if (focClearedCount === applicableCount) {
    return enteredActivationCount > 0
      ? GROUP_SUMMARY_STATUS.PENDING_ACTIVATION
      : GROUP_SUMMARY_STATUS.FOC_RECEIVED;
  }
  if (enteredFocProcessCount > 0) return GROUP_SUMMARY_STATUS.FOC_PENDING;
  return GROUP_SUMMARY_STATUS.NEW;
}
